/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AdviserResponseDto } from '../models/AdviserResponseDto';
import type { CreateUserDto } from '../models/CreateUserDto';
import type { PaginatedAdvisersResponseDto } from '../models/PaginatedAdvisersResponseDto';
import type { UpdateAdviserDto } from '../models/UpdateAdviserDto';
import type { UserManagementStatsResponseDto } from '../models/UserManagementStatsResponseDto';
import type { UserResponseDto } from '../models/UserResponseDto';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class UsersService {
    /**
     * List all users/advisers
     *
     * Retrieves a paginated list of users (advisers) with optional filtering and sorting.
     *
     * **Authorization Required:**
     * - Requires JWT authentication token
     * - Requires BUSINESS_ADMIN, SUPER_ADMIN, or ADVISER role
     *
     * **Role-based Access:**
     * - SUPER_ADMIN / BUSINESS_ADMIN: Can see all users from all FAPs and roles
     * - ADVISER: Can see their own user record; INTERNAL_ADVISER can also query adviser directory records
     *
     * **Filtering Options:**
     * - search: Filter by name, email, FSPR number, or FAP name/license number (case-insensitive)
     * - status: Filter by user status (active, inactive)
     * - fapId: Filter by FAP UUID (optional; SUPER_ADMIN and BUSINESS_ADMIN)
     * - role: Filter by user role (ADVISER, BUSINESS_ADMIN, SUPER_ADMIN)
     *
     * **Sorting:**
     * - sortBy: Field to sort by (name, emailAddress, createdAt, updatedAt, status, lastLogin)
     * - sortOrder: asc or desc
     *
     * **Pagination:**
     * - page: Page number (1-based, default: 1)
     * - limit: Items per page (1-100, default: 10)
     *
     * @param page Page number (1-based)
     * @param limit Number of items per page
     * @param search Search term to filter by first name, last name, email, adviser FSPR number, or FAP name/license number
     * @param status Filter by user status
     * @param fapId Filter by FAP ID
     * @param role Filter by user role
     * @param adviserRole Filter by adviser roles. Supports repeated query params or comma-separated values
     * @param sortBy Sort field
     * @param sortOrder Sort order
     * @returns PaginatedAdvisersResponseDto List of users/advisers with pagination metadata
     * @throws ApiError
     */
    public static usersControllerFindAll(
        page: number = 1,
        limit: number = 10,
        search?: string,
        status?: 'active' | 'inactive',
        fapId?: string,
        role?: 'SUPER_ADMIN' | 'BUSINESS_ADMIN' | 'FINANCE_ADMIN' | 'ADVISER',
        adviserRole?: Array<'AUTHORISED_REP' | 'QUOTING_ADVISER' | 'INTERNAL_ADVISER'>,
        sortBy: 'firstName' | 'lastName' | 'emailAddress' | 'createdAt' | 'updatedAt' | 'status' | 'lastLogin' = 'createdAt',
        sortOrder: 'asc' | 'desc' = 'desc',
    ): CancelablePromise<PaginatedAdvisersResponseDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/users',
            query: {
                'page': page,
                'limit': limit,
                'search': search,
                'status': status,
                'fapId': fapId,
                'role': role,
                'adviserRole': adviserRole,
                'sortBy': sortBy,
                'sortOrder': sortOrder,
            },
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                403: `Forbidden - user does not have required role`,
            },
        });
    }
    /**
     * Create a new user
     *
     * Creates a new user in both Auth0 and the database.
     *
     * **Authorization Required:**
     * - Requires JWT authentication token
     * - Requires one of the following roles: BUSINESS_ADMIN or SUPER_ADMIN
     *
     * **Process:**
     * 1. Validates the creator has required permissions
     * 2. Creates user in Auth0 for login-capable roles (skipped for AUTHORISED_REP)
     * 3. Assigns roles to user in Auth0 (if roles provided and Auth0 user was created)
     * 4. Creates user record in database
     * 5. Returns the created user with all details
     *
     * **Notes:**
     * - Login-capable emails must be unique (409 if already exists)
     * - AUTHORISED_REP users are contact-only (no Auth0/login) and may share an email with other AUTHORISED_REP users
     * - Password is optional (can be set later or user can use social login); not used for AUTHORISED_REP
     * - Roles must be valid UserRole enum values: SUPER_ADMIN, BUSINESS_ADMIN, or ADVISER
     * - fapLicenceNumber is required when role is ADVISER
     * - BUSINESS_ADMIN and SUPER_ADMIN without fapLicenceNumber receive app.fap.defaultId (servicing attribution only)
     * - BUSINESS_ADMIN creators can only create ADVISER users (must supply fapLicenceNumber)
     * - Only SUPER_ADMIN can create admin accounts (SUPER_ADMIN, FINANCE_ADMIN, BUSINESS_ADMIN)
     * - If role assignment fails in Auth0, user creation continues (roles can be assigned manually later)
     *
     * @param requestBody User creation data
     * @returns UserResponseDto User created successfully in both Auth0 and database
     * @throws ApiError
     */
    public static usersControllerCreateUser(
        requestBody: CreateUserDto,
    ): CancelablePromise<UserResponseDto> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/users',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request - invalid input data or validation failed`,
                403: `Forbidden - user does not have required role (BUSINESS_ADMIN or SUPER_ADMIN)`,
                409: `Conflict - user with email already exists in database`,
            },
        });
    }
    /**
     * Verify login email is registered
     * Public endpoint for the login UI before Auth0 redirect. Enforce access with an Auth0 Post-Login Action (see docs/api/GOOGLE_LOGIN_SETUP.md); this check can be bypassed if users hit Auth0 directly.
     * @param email
     * @returns any User exists
     * @throws ApiError
     */
    public static usersControllerVerifyLoginEmail(
        email: string,
    ): CancelablePromise<{
        id?: string;
        email?: string;
        firstName?: string;
        lastName?: string;
        role?: string;
        isActive?: boolean;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/users/verify',
            query: {
                'email': email,
            },
            errors: {
                400: `Missing email query parameter`,
                404: `No registered user with this email`,
            },
        });
    }
    /**
     * Check user exists for Auth0 Post-Login Action
     * Requires header x-quotehub-action-secret matching QUOTEHUB_ACTION_SHARED_SECRET. Used by Auth0 Actions only.
     * @param email
     * @param xQuotehubActionSecret
     * @returns any Lookup result
     * @throws ApiError
     */
    public static usersControllerExistsForAuth0Action(
        email: string,
        xQuotehubActionSecret: string,
    ): CancelablePromise<{
        exists?: boolean;
        authId?: string;
    }> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/users/exists',
            headers: {
                'x-quotehub-action-secret': xQuotehubActionSecret,
            },
            query: {
                'email': email,
            },
            errors: {
                401: `Invalid or missing action secret`,
            },
        });
    }
    /**
     * Get current authenticated user
     *
     * Retrieves the current authenticated user's profile.
     *
     * **Authorization Required:**
     * - Requires JWT authentication token
     * - Requires BUSINESS_ADMIN, SUPER_ADMIN, or ADVISER role
     *
     * **Includes:**
     * - User details
     * - Associated FAP information
     * - Adviser role/FSPR details (if linked)
     *
     * @returns AdviserResponseDto Current user found
     * @throws ApiError
     */
    public static usersControllerFindMe(): CancelablePromise<AdviserResponseDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/users/me',
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                403: `Forbidden - user does not have required role or profile is missing`,
            },
        });
    }
    /**
     * Get users management tab counts
     *
     * Returns collated counts for the users management page tabs in a single request.
     *
     * **Authorization Required:**
     * - Requires JWT authentication token
     * - Requires BUSINESS_ADMIN or SUPER_ADMIN role
     *
     * **Counts returned:**
     * - faps: FAPs visible to the current user
     * - advisers: Advisers visible to the current user
     * - businessAdmins: Business admins visible to the current user
     * - superAdmins: Super admin users (non-zero for SUPER_ADMIN only)
     * - financeAdmins: Finance admin users (non-zero for SUPER_ADMIN only)
     *
     * @returns UserManagementStatsResponseDto Collated users management tab counts
     * @throws ApiError
     */
    public static usersControllerGetManagementStats(): CancelablePromise<UserManagementStatsResponseDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/users/stats',
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                403: `Forbidden - user does not have required role`,
            },
        });
    }
    /**
     * Get a user/adviser by ID
     *
     * Retrieves a single user/adviser by their UUID.
     *
     * **Authorization Required:**
     * - Requires JWT authentication token
     * - Requires BUSINESS_ADMIN or SUPER_ADMIN role
     *
     * **Role-based Access:**
     * - SUPER_ADMIN / BUSINESS_ADMIN: Can view any user
     *
     * **Includes:**
     * - User details
     * - Associated FAP information
     * - Adviser FSPR number (if linked)
     *
     * @param id User UUID
     * @returns AdviserResponseDto User/adviser found
     * @throws ApiError
     */
    public static usersControllerFindOne(
        id: string,
    ): CancelablePromise<AdviserResponseDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/users/{id}',
            path: {
                'id': id,
            },
            errors: {
                401: `Unauthorized - JWT token missing or invalid`,
                403: `Forbidden - user does not have required role or access to this user`,
                404: `User not found`,
            },
        });
    }
    /**
     * Update adviser details
     *
     * Updates adviser information by user ID.
     *
     * **Authorization Required:**
     * - Requires JWT authentication token
     * - Requires BUSINESS_ADMIN or SUPER_ADMIN role
     *
     * **Role-based Access:**
     * - SUPER_ADMIN: Can update all users and can reassign FAP
     * - BUSINESS_ADMIN: Can update advisers and business admins business-wide
     * - BUSINESS_ADMIN cannot update FAP assignment (SUPER_ADMIN only)
     * - BUSINESS_ADMIN cannot change adviser type (SUPER_ADMIN only)
     *
     * **Editable fields:**
     * - firstName
     * - lastName
     * - emailAddress
     * - fsprNumber
     * - fapId (SUPER_ADMIN only)
     * - adviserRole (SUPER_ADMIN only)
     * - password (only when changing AUTHORISED_REP to a login-capable type)
     *
     * **Important:**
     * - Updating emailAddress also updates the Auth0 login email for this user.
     * - Changing AUTHORISED_REP to QUOTING_ADVISER/INTERNAL_ADVISER enables portal login:
     * a legacy blocked Auth0 user is unblocked, otherwise an Auth0 user is created (password required).
     * - Changing an adviser to AUTHORISED_REP blocks their Auth0 login.
     * - Frontend should show a warning before email changes because login email changes too.
     *
     * @param id Adviser user UUID
     * @param requestBody Adviser update data
     * @returns AdviserResponseDto Adviser updated successfully
     * @throws ApiError
     */
    public static usersControllerUpdateAdviser(
        id: string,
        requestBody: UpdateAdviserDto,
    ): CancelablePromise<AdviserResponseDto> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/v1/users/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Bad request - invalid input data`,
                401: `Unauthorized - JWT token missing or invalid`,
                403: `Forbidden - insufficient permissions for this adviser update`,
                404: `Adviser user not found`,
                409: `Conflict - another user already has this email address`,
            },
        });
    }
}
